import { ActionMap } from "library/ActionMap";
import { errorMessageMap } from "library/hooks/useDebitCardPayment/errorMessageMap";

type Status = "LOADING" | "PROCESSING_PAYMENT" | "READY" | "ERROR" | "SUCCESS";

type ErrorType =
  | "CRITICAL"
  | "INSUFFICIENT_FUNDS"
  | "CARD_DOES_NOT_MATCH_REGION"
  | "PAYMENT_DECLINE"
  | "EXCEED_CREDIT_LIMIT";

export interface DebitCardError {
  type: ErrorType;
  message: string;
}

export interface DebitCardPaymentState {
  error?: {
    type: ErrorType;
    message: string;
  };
  statusUrl: string;
  receiptNumber: string;
  status: Status;
  cardNumber: string;
  cardScheme: string;
}

export const initialState: DebitCardPaymentState = {
  error: undefined,
  statusUrl: "",
  receiptNumber: "",
  status: "LOADING",
  cardNumber: "",
  cardScheme: "",
};

// eslint-disable-next-line no-shadow
export enum DebitCardActionType {
  QUICK_STREAM_READY = "QUICK_STREAM_READY",
  TOKEN_RECEIVED = "TOKEN_RECEIVED",
  TOKEN_ERROR = "TOKEN_ERROR",
  SAVED_PAYMENT_INSTRUMENT_RECEIVED = "SAVED_PAYMENT_INSTRUMENT_RECEIVED",
  SUBMIT_PAYMENT = "SUBMIT_PAYMENT",
  PAYMENT_POST_SUCCESS = "PAYMENT_POST_SUCCESS",
  PAYMENT_POST_FAILURE = "PAYMENT_POST_FAILURE",
  PAYMENT_APPROVED = "PAYMENT_APPROVED",
  PAYMENT_DECLINED = "PAYMENT_DECLINED",
  INSUFFICIENT_FUNDS = "INSUFFICIENT_FUNDS",
  EXCEED_CREDIT_LIMIT = "EXCEED_CREDIT_LIMIT",
  CARD_DOES_NOT_MATCH_REGION = "CARD_DOES_NOT_MATCH_REGION",
  PAYMENT_STATUS_FAILURE = "PAYMENT_STATUS_FAILURE",
}

type DebitCardPayloads = {
  [DebitCardActionType.PAYMENT_POST_SUCCESS]: { statusUrl: string };
  [DebitCardActionType.TOKEN_RECEIVED]: {
    cardNumber: string;
    cardScheme: string;
  };
  [DebitCardActionType.SAVED_PAYMENT_INSTRUMENT_RECEIVED]: {
    cardNumber: string;
    cardScheme: string;
  };
  [DebitCardActionType.PAYMENT_POST_FAILURE]: { errorMessage: string };
  [DebitCardActionType.PAYMENT_APPROVED]: { receiptNumber: string };
  [DebitCardActionType.CARD_DOES_NOT_MATCH_REGION]: { region: "AU" | "NZ" };
};

export type DebitCardAction = ActionMap<DebitCardActionType, DebitCardPayloads>;

export const DebitCardPaymentReducer = (
  state: DebitCardPaymentState,
  action: DebitCardAction,
): DebitCardPaymentState => {
  switch (action.type) {
    case DebitCardActionType.QUICK_STREAM_READY:
      return {
        ...state,
        status: "READY",
      };
    case DebitCardActionType.TOKEN_RECEIVED:
    case DebitCardActionType.SAVED_PAYMENT_INSTRUMENT_RECEIVED:
      return {
        ...state,
        cardNumber: action.cardNumber,
        cardScheme: action.cardScheme,
      };
    case DebitCardActionType.TOKEN_ERROR:
      return {
        ...state,
        cardNumber: "",
        cardScheme: "",
        status: "READY",
      };
    case DebitCardActionType.SUBMIT_PAYMENT:
      return {
        ...state,
        receiptNumber: "",
        cardNumber: "",
        cardScheme: "",
        status: "PROCESSING_PAYMENT",
        error: undefined,
      };
    case DebitCardActionType.PAYMENT_POST_SUCCESS:
      return { ...state, statusUrl: action.statusUrl };
    case DebitCardActionType.PAYMENT_POST_FAILURE:
      return {
        ...state,
        status: "ERROR",
        error: {
          type: "CRITICAL",
          message: "Payment post has failed",
        },
        cardNumber: "",
        cardScheme: "",
      };
    case DebitCardActionType.PAYMENT_STATUS_FAILURE:
      return {
        ...state,
        status: "ERROR",
        error: {
          type: "CRITICAL",
          message: "Payment status request failed",
        },
        cardNumber: "",
        cardScheme: "",
      };
    case DebitCardActionType.PAYMENT_APPROVED:
      return {
        ...state,
        status: "SUCCESS",
        receiptNumber: action.receiptNumber,
        statusUrl: "",
      };
    case DebitCardActionType.PAYMENT_DECLINED:
      return {
        ...state,
        statusUrl: "",
        status: "ERROR",
        error: {
          type: "PAYMENT_DECLINE",
          message: errorMessageMap.paymentDecline,
        },
        cardNumber: "",
        cardScheme: "",
      };
    case DebitCardActionType.INSUFFICIENT_FUNDS:
      return {
        ...state,
        statusUrl: "",
        status: "ERROR",
        error: {
          type: "INSUFFICIENT_FUNDS",
          message: errorMessageMap.insufficientFunds,
        },
        cardNumber: "",
        cardScheme: "",
      };
    case DebitCardActionType.EXCEED_CREDIT_LIMIT:
      return {
        ...state,
        statusUrl: "",
        status: "ERROR",
        error: {
          type: "EXCEED_CREDIT_LIMIT",
          message: errorMessageMap.exceedCreditLimit,
        },
        cardNumber: "",
        cardScheme: "",
      };
    case DebitCardActionType.CARD_DOES_NOT_MATCH_REGION:
      return {
        ...state,
        statusUrl: "",
        status: "ERROR",
        error: {
          type: "CARD_DOES_NOT_MATCH_REGION",
          message:
            action.region === "AU"
              ? errorMessageMap.cardDoesNotMatchRegionAu
              : errorMessageMap.cardDoesNotMatchRegionNz,
        },
        cardNumber: "",
        cardScheme: "",
      };
    default:
      return state;
  }
};
